import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { STORES_API_BASE, isNotAvailable, useEffectiveStoreNo } from './useStores';

/**
 * Store paperwork (ISD-575). Photos of vendor invoices and licenses go to the
 * store's Store Info files in Cockpit (Paperwork/<category>), where Store Ops
 * reviews them. CINTAS is expected twice a month, Pest Control once.
 */

export type PaperworkOption = {
  code: string;
  label: string;
  /** Non-empty for Licenses; the store picks one and its code is what gets sent. */
  types: { code: string; label: string }[];
};

export type PaperworkStatus = {
  store_no: string;
  period: string;
  /** Tracked categories only (need > 0), for the "this month" card. */
  categories: { code: string; label: string; have: number; need: number }[];
  /** Every button the store sees. */
  options: PaperworkOption[];
  recent: {
    id: number;
    category: string | null;
    label: string;
    doc_date: string | null;
    files: number;
    created_at: string | null;
  }[];
};

const STATUS_KEY = ['stores', 'paperwork', 'status'] as const;

export function usePaperworkStatus(): UseQueryResult<PaperworkStatus | null, Error> {
  const storeNo = useEffectiveStoreNo();
  return useQuery<PaperworkStatus | null, Error>({
    queryKey: [...STATUS_KEY, storeNo ?? 'own'],
    queryFn: async () => {
      const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
      try {
        return await apiGet<PaperworkStatus>(`${STORES_API_BASE}/paperwork/status${qs}`);
      } catch (err) {
        if (isNotAvailable(err)) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Items still due this month, for the Home card. */
export function paperworkStillDue(status: PaperworkStatus | null | undefined): number {
  return (status?.categories ?? []).reduce((n, c) => n + Math.max(0, c.need - c.have), 0);
}

type SubmitResult = { id: number; folder_id: number; files: { file_id: number; upload_url: string }[] };

/** `category` is an option code, or the chosen type's code when the option has types. */
export type PaperworkSubmission = {
  category: string;
  doc_date?: string;
  note?: string;
};

/**
 * Send one invoice or license with its photos. Every photo must land: a record
 * Store Ops cannot see is worse than an error the store can retry, so a failed
 * upload throws, and only uploaded photos are marked ready.
 */
export function useSubmitPaperwork() {
  const qc = useQueryClient();
  const storeNo = useEffectiveStoreNo();
  return useMutation({
    mutationFn: async ({ body, files }: { body: PaperworkSubmission; files: File[] }) => {
      const typed = (f: File) => f.type || 'application/octet-stream';
      const store = storeNo !== undefined ? { store_no: String(storeNo) } : {};
      const res = await apiPost<SubmitResult>(`${STORES_API_BASE}/paperwork`, {
        ...body,
        ...store,
        files: files.map((f) => ({ name: f.name, content_type: typed(f), size_bytes: f.size })),
      });
      if (res.files.length !== files.length) throw new Error('upload links missing');
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const put = await fetch(res.files[i]!.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': typed(file) },
          body: file,
        });
        if (!put.ok) throw new Error(`upload failed for ${file.name}`);
      }
      await apiPost(`${STORES_API_BASE}/paperwork/${res.id}/complete`, {
        file_ids: res.files.map((f) => f.file_id),
        ...store,
      });
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: STATUS_KEY }),
  });
}
